import { ApolloError } from '@apollo/client';

/**
 * Extracts user-friendly error message and action from GraphQL errors.
 *
 * For Amadeus errors, the backend sends:
 * - error.message: User-friendly message (from AmadeusError.userMessage)
 * - error.extensions.userAction: Actionable guidance for the user
 *
 * @param error - Apollo error or Error object
 * @returns Object with userMessage and userAction (if available)
 */
export function extractUserFriendlyError(error: ApolloError | Error): {
  message: string;
  action?: string;
} {
  // ApolloError has graphQLErrors array
  if ('graphQLErrors' in error && error.graphQLErrors?.length > 0) {
    const graphQLError = error.graphQLErrors[0];
    const extensions = graphQLError.extensions || {};

    return {
      message: graphQLError.message || error.message,
      action: extensions.userAction as string | undefined,
    };
  }

  // Regular Error object - check if it has extensions (from GraphQL)
  if ('extensions' in error && error.extensions) {
    const extensions = error.extensions as Record<string, unknown>;
    return {
      message: error.message,
      action: extensions.userAction as string | undefined,
    };
  }

  // Fallback to error message
  return {
    message: error.message || 'An unexpected error occurred',
  };
}

/**
 * Formats error message for display, including user action if available.
 *
 * @param error - Apollo error or Error object
 * @returns Formatted error message string
 */
export function formatErrorForDisplay(error: ApolloError | Error): string {
  const { message, action } = extractUserFriendlyError(error);

  if (action) {
    return `${message} ${action}`;
  }

  return message;
}
